import React from 'react';
import Box from '@mui/material/Box';
import Dialog from '@mui/material/Dialog';
import apiClient, { getAllPerspectives, getModel } from '../apis';
import useVoyagerStore from '../stores/useVoyagerStore';
import type { Features } from '../stores/slices/featuresSlice';
import type { Pin } from '../stores/slices/pinsSlice';
import type { ConversationThread } from '../types';
import { composerRoute } from '../components/LandingAgent/composerRouting';
import { handDraft } from '../components/LandingAgent/composerDrafts';
import { activeAt } from './recents';
import { startNewQuestion, useChatSidebar } from './chatSidebarState';
import { useSimpleSidebar } from './simpleSidebarState';
import { SIMPLE_DESTINATIONS } from './SimpleSidebar';
import { useSearchPalette } from './searchPaletteState';

/**
 * The search palette (#1514), on ⌘K and the sidebar's Search. Its first row is always "Ask Stroma: <typed text>",
 * so ⌘K, typing and Enter asks (what #765 protected). Below it: the viewer's own
 * chats, the metrics of the perspectives their catalog opens now, their findings
 * (claims the server reads for them, and number pins whose perspective is in
 * that catalog read), and the pages. Everything listed is read as the viewer
 * when the palette opens; nothing is a cached copy of someone else's.
 */

export type PaletteGroup = 'Ask' | 'Chats' | 'Metrics' | 'Findings' | 'Pages';
export interface PaletteRow { key: string; group: PaletteGroup; label: string; sub?: string; run: () => void }

export interface PaletteMetric { perspectiveId: string; perspectiveName: string; domain: string; name: string; label: string }
export interface PaletteClaim { id: string; quote: string; speaker: string }

/** What the palette lists from, each read as the viewer. */
export interface PaletteSources {
  threads: ConversationThread[];
  /** null while the catalog read is pending or after it failed: then no metrics and no number pins */
  metrics: PaletteMetric[] | null;
  /** perspective ids the viewer's catalog opens, from the same read (null: pending or failed) */
  perspectives: ReadonlySet<string> | null;
  pins: Pin[];
  claims: PaletteClaim[];
}

/** How many of each group a query lists. */
export const PER_GROUP = 6;

const matches = (q: string, ...texts: Array<string | null | undefined>) => !q || texts.some((t) => !!t && t.toLowerCase().includes(q));

/** Leaves a placeholder page and the phone drawer, as the sidebar does. */
function leave(): void {
  useSimpleSidebar.getState().showPlaceholder(null);
  useChatSidebar.getState().setDrawerOpen(false);
}

/** Ask Stroma: a new chat, the question asked in it. An addressed or refused text waits in the new chat's composer. */
export function askFromPalette(text: string): void {
  const q = text.trim();
  startNewQuestion();
  if (!q) return;
  const s = useVoyagerStore.getState();
  if (composerRoute(q, s.agents || []).kind === 'voyager' && s.sendLandingAgentQuery(q) !== false) return;
  handDraft('home', q);
}

function openThread(id: string): void {
  const s = useVoyagerStore.getState();
  useVoyagerStore.setState({ objectiveSpaceId: null } as any);
  if (s.currentThreadId !== id) s.loadThread(id);
  else useVoyagerStore.setState({ currentView: 'landing' } as any);
  leave();
}

/**
 * A number pin the viewer may still see and open: its perspective is in their catalog as read now, and the chat it
 * was pinned from is still one of theirs (opening the pin opens that chat). Quotes and suggestions are not listed.
 */
export function pinListed(pin: Pin, perspectives: ReadonlySet<string> | null, threads: ConversationThread[]): boolean {
  if (pin.suggestion || pin.claim.span || !perspectives) return false;
  if (!pin.threadId || !threads.some((t) => t.id === pin.threadId && (!t.kind || t.kind === 'thread'))) return false;
  const perspective = pin.claim.grounding?.perspective;
  return !!perspective && perspectives.has(perspective);
}

const PAGES: Array<{ key: string; label: string; go: () => void; shown?: (f: Features) => boolean }> = [
  ...SIMPLE_DESTINATIONS.filter((d) => d.id !== 'search').map((d) => ({ key: d.id, label: d.label, go: d.go, shown: d.shown })),
  { key: 'history', label: 'All chats', go: () => useVoyagerStore.getState().navigateToHistory() },
  { key: 'settings', label: 'Settings', go: () => useVoyagerStore.getState().navigateToAgentStudio('appearance') },
];

/** The rows for a query, in order: Ask first, always; then Chats, Metrics, Findings, Pages. */
export function paletteRows(text: string, src: PaletteSources): PaletteRow[] {
  const q = text.trim().toLowerCase();
  const typed = text.trim();
  const rows: PaletteRow[] = [{ key: 'ask', group: 'Ask', label: typed ? `Ask Stroma: ${typed}` : 'Ask Stroma', run: () => askFromPalette(typed) }];
  const chats = src.threads
    .filter((t) => (!t.kind || t.kind === 'thread') && matches(q, t.title))
    .sort((a, b) => activeAt(b) - activeAt(a))
    .slice(0, PER_GROUP);
  rows.push(...chats.map((t): PaletteRow => ({ key: `chat:${t.id}`, group: 'Chats', label: t.title || 'Untitled chat', run: () => openThread(t.id) })));
  if (q) {
    const metrics = (src.metrics || []).filter((m) => matches(q, m.label, m.name)).slice(0, PER_GROUP);
    rows.push(...metrics.map((m): PaletteRow => ({ key: `metric:${m.perspectiveId}:${m.name}`, group: 'Metrics', label: m.label, sub: m.perspectiveName,
      run: () => { leave(); void useVoyagerStore.getState().navigateToWorkspace(m.domain, m.perspectiveId); } })));
    const pins = src.pins.filter((p) => pinListed(p, src.perspectives, src.threads) && matches(q, p.claim.label, p.claim.grounding?.metric));
    const findings: PaletteRow[] = [
      ...pins.map((p): PaletteRow => ({ key: `pin:${p.id}`, group: 'Findings', label: p.claim.label || p.claim.grounding?.metric || 'Pinned number', sub: 'Pinned',
        run: () => openThread(p.threadId as string) })),
      // the server already matched these to the query, as the viewer, at their bar
      ...src.claims.map((c): PaletteRow => ({ key: `claim:${c.id}`, group: 'Findings', label: c.quote, sub: c.speaker,
        run: () => { leave(); useVoyagerStore.getState().navigateToBrain('claims'); } })),
    ];
    rows.push(...findings.slice(0, PER_GROUP));
  }
  const features = useVoyagerStore.getState().features;
  rows.push(...PAGES.filter((p) => (!p.shown || p.shown(features)) && matches(q, p.label)).map((p): PaletteRow => ({ key: `page:${p.key}`, group: 'Pages', label: p.label, run: () => { leave(); p.go(); } })));
  return rows;
}

/** The viewer's catalog, read when the palette opens: perspectives, then each one's metrics (own and inherited). */
function useCatalog(): { metrics: PaletteMetric[] | null; perspectives: ReadonlySet<string> | null } {
  const [state, setState] = React.useState<{ metrics: PaletteMetric[] | null; perspectives: ReadonlySet<string> | null }>({ metrics: null, perspectives: null });
  React.useEffect(() => {
    let live = true;
    getAllPerspectives().then(async (list) => {
      const ps = (Array.isArray(list) ? list : []).filter((p: any) => typeof p?.id === 'string' && p.id);
      if (!live) return;
      setState({ metrics: null, perspectives: new Set(ps.map((p: any) => p.id)) });
      const models = await Promise.allSettled(ps.map((p: any) => getModel(p.id)));
      if (!live) return;
      const seen = new Set<string>();
      const metrics: PaletteMetric[] = [];
      models.forEach((r, i) => {
        if (r.status !== 'fulfilled' || !r.value) return;
        const p: any = ps[i];
        const all = [...(r.value.metrics || []), ...(r.value.inheritedModels || []).flatMap((m) => m.metrics || [])];
        for (const m of all) {
          if (!m?.name || seen.has(`${p.id}:${m.name}`)) continue;
          seen.add(`${p.id}:${m.name}`);
          metrics.push({ perspectiveId: p.id, perspectiveName: p.name || p.id, domain: p.domain || '', name: m.name, label: m.display_name || m.name });
        }
      });
      setState((s) => ({ ...s, metrics }));
    }, () => undefined);
    return () => { live = false; };
  }, []);
  return state;
}

/** Claims the server finds for the query, as the viewer; none until it answers. */
function useClaims(text: string): PaletteClaim[] {
  const [claims, setClaims] = React.useState<PaletteClaim[]>([]);
  const q = text.trim();
  React.useEffect(() => {
    setClaims([]);
    if (q.length < 2) return undefined;
    let live = true;
    const timer = window.setTimeout(() => {
      apiClient.get<any>('/api/knowledge/claims', { params: { by: 'metric', q, limit: PER_GROUP } }).then((res) => {
        const rows = Array.isArray(res?.data?.rows) ? res.data.rows : [];
        if (live) setClaims(rows.filter((r: any) => typeof r?.claim_id === 'string' && typeof r?.quote === 'string')
          .map((r: any) => ({ id: r.claim_id, quote: r.quote, speaker: typeof r.speaker === 'string' ? r.speaker : '' })));
      }, () => undefined);
    }, 250);
    return () => { live = false; window.clearTimeout(timer); };
  }, [q]);
  return claims;
}

/** Mounted only while open, so each opening reads afresh and closing leaves nothing behind. */
export default function SearchPalette() {
  const open = useSearchPalette((s) => s.open);
  return open ? <Palette /> : null;
}

function Palette() {
  const threads = useVoyagerStore((s) => s.savedThreads);
  const pins = useVoyagerStore((s) => s.pins);
  const [text, setText] = React.useState('');
  const [hl, setHl] = React.useState(0);
  const { metrics, perspectives } = useCatalog();
  const claims = useClaims(text);
  const rows = React.useMemo(() => paletteRows(text, { threads, metrics, perspectives, pins, claims }), [text, threads, metrics, perspectives, pins, claims]);
  const close = () => useSearchPalette.getState().setOpen(false);
  const run = (row: PaletteRow | undefined) => { if (!row) return; close(); row.run(); };
  const shown = Math.min(hl, rows.length - 1);
  const onKey = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown') { e.preventDefault(); setHl(Math.min(shown + 1, rows.length - 1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setHl(Math.max(shown - 1, 0)); }
    else if (e.key === 'Enter' && !e.nativeEvent.isComposing) { e.preventDefault(); run(rows[shown]); }
  };
  let group: PaletteGroup | null = null;
  return <Dialog open onClose={close} transitionDuration={0} aria-label="Search" data-testid="search-palette"
    sx={{ '& .MuiDialog-container': { alignItems: 'flex-start', pt: '13vh' }, '& .MuiBackdrop-root': { bgcolor: 'rgba(20,20,18,.25)' } }}
    PaperProps={{ sx: { width: '100%', maxWidth: 600, m: 2, bgcolor: 'var(--bg)', border: '1px solid var(--line)', borderRadius: '14px', boxShadow: 'var(--sh-3)', overflow: 'hidden', backgroundImage: 'none' } }}>
    <Box component="input" autoFocus value={text} placeholder="Search chats, metrics, findings and pages, or ask…"
      role="combobox" aria-label="Search or ask" aria-expanded aria-controls="search-palette-list" aria-activedescendant={rows[shown] ? `palette-row-${shown}` : undefined}
      onChange={(e: React.ChangeEvent<HTMLInputElement>) => { setText(e.target.value); setHl(0); }} onKeyDown={onKey}
      sx={{ width: '100%', border: 0, outline: 0, px: 2.25, py: 2, fontSize: 15, font: 'inherit', bgcolor: 'transparent', color: 'var(--ink)', borderBottom: '1px solid var(--line)' }} />
    <Box id="search-palette-list" role="listbox" aria-label="Results" sx={{ maxHeight: 360, overflow: 'auto', p: 0.75 }}>
      {rows.map((row, i) => {
        const head = row.group !== 'Ask' && row.group !== group ? row.group : null;
        group = row.group;
        return <React.Fragment key={row.key}>
          {head && <Box role="presentation" sx={{ fontSize: 11.5, color: 'var(--ink-3)', px: 1.25, pt: 1, pb: 0.5 }}>{head}</Box>}
          <Box id={`palette-row-${i}`} role="option" aria-selected={i === shown} data-group={row.group} onMouseMove={() => setHl(i)} onClick={() => run(row)}
            sx={{ display: 'flex', alignItems: 'center', gap: 1.25, px: 1.25, py: 1.125, borderRadius: '8px', cursor: 'pointer', fontSize: 13.5, color: 'var(--ink)', bgcolor: i === shown ? 'var(--hover)' : 'transparent' }}>
            <Box component="span" sx={{ flex: 1, minWidth: 0, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', fontWeight: row.group === 'Ask' ? 500 : 400 }}>{row.label}</Box>
            {row.sub && <Box component="span" sx={{ fontSize: 12, color: 'var(--ink-3)', whiteSpace: 'nowrap' }}>{row.sub}</Box>}
            {row.group === 'Ask' && <Box component="kbd" sx={{ fontFamily: 'var(--mono)', fontSize: 10.5, color: 'var(--ink-3)', border: '1px solid var(--line)', borderRadius: '4px', px: 0.5 }}>↵</Box>}
          </Box>
        </React.Fragment>;
      })}
    </Box>
  </Dialog>;
}
